import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useProjectCtx } from "@/context/ProjectContext";
import { PageHeader } from "@/components/PageHeader";
import { EmptyState } from "@/components/EmptyState";
import { Input } from "@/components/ui/input";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from "@/components/ui/table";
import { fmtNum, todayISO, addDaysISO } from "@/lib/format";
import { getErrorMessage } from "@/lib/errors";
import { ResourcePlanTools } from "@/pages/project/ResourcePlanTools";

export default function ResourcePlanning() {
  const { projectId, isProjectManager } = useProjectCtx();
  const [range, setRange] = useState({ start: addDaysISO(todayISO(), -30), end: todayISO() });
  const [activities, setActivities] = useState([]);
  const [wbsItemId, setWbsItemId] = useState("__all__");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { date_from: range.start, date_to: range.end };
      if (wbsItemId !== "__all__") params.wbs_item_id = wbsItemId;
      const [{ data }, { data: acts }] = await Promise.all([
        api.get(`/projects/${projectId}/resource-planning`, { params }),
        api.get(`/projects/${projectId}/construction`),
      ]);
      setRows(data);
      setActivities(acts.filter((a) => !a.is_group));
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  }, [projectId, range, wbsItemId]);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="space-y-4" data-testid="resource-planning-page">
      <PageHeader
        title="Resource Planning"
        description="Planned and actual manpower and machinery by trade over the selected dates. Actuals come from Labour Management attendance and the Equipment Logbook; planned counts are calculated from each activity's daily plan."
        action={
          <div className="flex items-center gap-2">
            <Select value={wbsItemId} onValueChange={setWbsItemId}>
              <SelectTrigger className="w-48" data-testid="resource-planning-activity-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__all__">All activities</SelectItem>
                {activities.map((a) => <SelectItem key={a.id} value={a.id}>{a.code} — {a.name}</SelectItem>)}
              </SelectContent>
            </Select>
            <Input type="date" value={range.start} onChange={(e) => setRange({ ...range, start: e.target.value })} data-testid="resource-planning-start-input" className="w-36" />
            <Input type="date" value={range.end} onChange={(e) => setRange({ ...range, end: e.target.value })} data-testid="resource-planning-end-input" className="w-36" />
          </div>
        }
      />

      <ResourcePlanTools projectId={projectId} activities={activities} canEdit={isProjectManager} onChanged={load} />

      <div className="rounded-xl border border-border bg-card">
        {!loading && rows.length === 0 ? (
          <EmptyState title="No resource data in this range" testId="resource-planning-empty-state" />
        ) : (
          <Table>
            <TableHeader>
              <TableRow><TableHead>Trade</TableHead><TableHead>Activity</TableHead><TableHead className="text-right">Planned</TableHead><TableHead className="text-right">Actual (person / machine days)</TableHead><TableHead className="text-right">Utilisation</TableHead></TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((r, i) => (
                <TableRow key={i} data-testid={`resource-planning-row-${i}`}>
                  <TableCell className="text-foreground">{r.trade}</TableCell>
                  <TableCell className="text-xs text-muted-foreground">{r.code ? `${r.code} — ${r.name}` : "All activities"}</TableCell>
                  <TableCell className="text-right font-mono text-xs text-muted-foreground">{Number(r.planned_total) ? fmtNum(r.planned_total, 2) : "—"}</TableCell>
                  <TableCell className="text-right font-mono text-xs text-foreground">{fmtNum(r.actual_total, 2)}</TableCell>
                  <TableCell className="text-right font-mono text-xs text-muted-foreground">{Number(r.planned_total) ? `${fmtNum((Number(r.actual_total) / Number(r.planned_total)) * 100, 0)}%` : "Not planned"}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>
    </div>
  );
}
